import { memo, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react'
import MDEditor from '@uiw/react-md-editor'
import { SaveIndicator } from './SaveIndicator'
import { useUIStore } from '../../store/uiStore'
import { useAutoSave } from '../../hooks/useAutoSave'
import { useSimpleHashtags } from '../../hooks/useSimpleHashtags'
import { cn } from '../../lib/utils'

interface EditorProps {
  noteId: string | null
  content: string
  title?: string
  onContentChange: (content: string) => void
  onTitleChange?: (title: string) => void
  className?: string
  showToolbar?: boolean
  autoFocus?: boolean
}

export interface EditorRef {
  focus: () => void
  getEditor: () => any // MDEditor doesn't expose the same interface as TipTap
}

export const Editor = memo(forwardRef<EditorRef, EditorProps>(({
  noteId,
  content,
  title,
  onContentChange,
  onTitleChange,
  className,
  showToolbar = true,
  autoFocus = false
}, ref) => {
  const {
    fontSize,
    fontFamily,
    lineHeight,
    enableSpellcheck
  } = useUIStore()

  // Track the current editor content locally
  const [currentContent, setCurrentContent] = useState(content)
  const [currentTitle, setCurrentTitle] = useState(title || '')
  
  // Simple hashtag extraction
  const { debouncedSyncNoteTags } = useSimpleHashtags()

  // Update local state when props change (note selection)
  useEffect(() => {
    setCurrentContent(content)
    setCurrentTitle(title || '')
  }, [noteId, content, title])

  // Handle content changes
  const handleContentChange = useCallback((value?: string) => {
    const newContent = value || ''
    setCurrentContent(newContent)
    onContentChange(newContent)
    
    // Sync hashtags when content changes (debounced)
    if (noteId) {
      // Extract plain text for hashtag detection
      // Remove markdown formatting for hashtag extraction
      const plainText = newContent.replace(/[#*_`~\[\]()]/g, '')
      debouncedSyncNoteTags(noteId, plainText)
    }
  }, [noteId, onContentChange, debouncedSyncNoteTags])

  // Handle title changes
  const handleTitleChange = (newTitle: string) => {
    setCurrentTitle(newTitle)
    onTitleChange?.(newTitle)
  }

  // Auto-save functionality using current content
  useAutoSave({
    noteId,
    content: currentContent,
    title: currentTitle,
    enabled: !!noteId
  })

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      // MDEditor doesn't expose a direct focus method
      // We'll try to focus the textarea element
      const textarea = document.querySelector('.w-md-editor-text-textarea') as HTMLTextAreaElement
      if (textarea) {
        textarea.focus()
      }
    },
    getEditor: () => {
      // Return a minimal interface since MDEditor doesn't expose the same methods as TipTap
      return {
        getHTML: () => currentContent,
        getText: () => currentContent.replace(/[#*_`~\[\]()]/g, ''),
        commands: {
          setContent: (content: string) => setCurrentContent(content),
          focus: () => {
            const textarea = document.querySelector('.w-md-editor-text-textarea') as HTMLTextAreaElement
            if (textarea) {
              textarea.focus()
            }
          }
        }
      }
    }
  }), [currentContent])

  // Custom styles for the editor
  const editorStyles = {
    fontSize,
    fontFamily,
    lineHeight,
    margin: '0 auto',
    width: '100%'
  }

  // Determine color mode based on system preference or theme
  const getColorMode = (): "light" | "dark" => {
    // if (typeof window !== 'undefined') {
    //   const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches ||
    //                 document.documentElement.classList.contains('dark')
    //   return isDark ? 'dark' : 'light'
    // }
    
    return 'light'
  }

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Editor */}
      <div 
        className="flex-1 overflow-y-auto flex flex-col"
        style={editorStyles}
      >
        {/* Title input (if title editing is enabled) */}
        {onTitleChange && (
          <div className="px-4 pt-4 pb-2 border-b border-border flex-shrink-0">
            <input
              type="text"
              value={currentTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Note title..."
              className={cn(
                'w-full bg-transparent border-none outline-none',
                'text-2xl font-bold placeholder:text-muted-foreground',
                'resize-none overflow-hidden'
              )}
              style={{
                fontSize: fontSize * 1.5,
                fontFamily,
                lineHeight
              }}
            />
          </div>
        )}

        {/* MDEditor content */}
        <div className="p-4 flex-1 flex flex-col">
          <MDEditor
            value={currentContent}
            onChange={handleContentChange}
            preview={'preview'}
            hideToolbar={!showToolbar}
            visibleDragbar={false}
            data-color-mode={getColorMode()}
            height="100%"
            style={{
              backgroundColor: 'transparent',
              height: '100%'
            }}
            textareaProps={{
              autoFocus,
              spellCheck: enableSpellcheck,
              style: {
                fontSize,
                fontFamily,
                lineHeight,
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none'
              }
            }}
          />
        </div>
      </div>

      {/* Save indicator */}
      <div className="flex justify-end p-2 border-t border-border flex-shrink-0">
        <SaveIndicator />
      </div>
    </div>
  )
}))

Editor.displayName = 'Editor'
